import React, { useRef, useCallback, useEffect } from 'react';
import { useCompare } from '@context/CompareContext';
import { useDiff } from '@hooks/useDiff';
import ComparePanel from './ComparePanel';
import CompareSummary from './CompareSummary';
import CompareControls from './CompareControls';
import DiffRenderer from './DiffRenderer';
import { X, Scale } from 'lucide-react';
import { IconButton, Button } from '../ui';

interface CompareViewProps {
  onClose?: () => void;
  onSelectFromHistory?: (slot: 'A' | 'B') => void;
  className?: string;
}

export function CompareView({
  onClose,
  onSelectFromHistory,
  className = '',
}: CompareViewProps): React.ReactElement | null {
  const {
    isActive, slotA, slotB, viewMode, showSections, syncScroll, highlightChanges,
    setViewMode, toggleSection, setSyncScroll, setHighlightChanges, swapSlots, closeCompare,
  } = useCompare();

  const panelARef = useRef<HTMLDivElement>(null);
  const panelBRef = useRef<HTMLDivElement>(null);

  // Use a ref instead of state to track the source of truth for scrolling
  // This prevents infinite loops without the delay of React state updates
  const activeScrollSource = useRef<'A' | 'B' | null>(null);
  const scrollTimeout = useRef<number | null>(null);

  const getTextToDiff = useCallback((slot: typeof slotA) => {
    if (!slot) return '';
    const parts: string[] = [];
    if (showSections.metadata) parts.push(`[TITLE] ${slot.title}`);
    if (showSections.lyrics) parts.push(slot.lyrics);
    if (showSections.vocals && slot.vocalPhysicsBlocks.length > 0) parts.push(...slot.vocalPhysicsBlocks);
    if (showSections.production && slot.productionNotes) parts.push(`[PRODUCTION NOTES]\n${slot.productionNotes}`);
    if (showSections.sunoTags && slot.sunoStyleTags && slot.sunoStyleTags.length > 0) parts.push(`[STYLE TAGS] ${slot.sunoStyleTags.join(', ')}`);
    return parts.join('\n\n');
  }, [showSections]);

  const textA = getTextToDiff(slotA);
  const textB = getTextToDiff(slotB);
  const diffResult = useDiff(textA, textB, { detectStructuralElements: true, wordLevelDiff: true });

  const handleScroll = useCallback((source: 'A' | 'B', scrollTop: number) => {
    if (!syncScroll) return;

    // If we are currently responding to a scroll initiated by the other panel, ignore it
    if (activeScrollSource.current && activeScrollSource.current !== source) {
      return;
    }

    // Mark this source as the initiator
    activeScrollSource.current = source;

    const targetRef = source === 'A' ? panelBRef : panelARef;
    if (targetRef.current) {
      targetRef.current.scrollTop = scrollTop;
    }

    // Reset the source after a short period of inactivity
    if (scrollTimeout.current) window.clearTimeout(scrollTimeout.current);
    scrollTimeout.current = window.setTimeout(() => {
      activeScrollSource.current = null;
    }, 50);
  }, [syncScroll]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isActive) return;
      if (e.key === 'Escape') closeCompare();
      else if (e.key === 's' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        swapSlots();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      if (scrollTimeout.current) window.clearTimeout(scrollTimeout.current);
    };
  }, [isActive, swapSlots, closeCompare]);

  if (!isActive) return null;

  return (
    <div className={`fixed inset-0 z-[120] flex flex-col bg-slate-950 animate-in fade-in duration-300 ${className}`}>
      <header className="flex items-center justify-between px-8 h-20 bg-slate-900 border-b border-slate-800 shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-accent text-slate-950 shadow-lg shadow-amber-900/20">
            <Scale size={20} />
          </div>
          <h2 className="text-3xl font-black text-slate-50 uppercase tracking-tighter">
            A/B Comparison <span className="text-accent">Engine</span>
          </h2>
        </div>
        <IconButton
          onClick={closeCompare}
          variant="ghost"
          size="md"
          aria-label="Close Comparison"
          className="rounded-xl"
        >
          <X size={24} />
        </IconButton>
      </header>

      <CompareControls
        viewMode={viewMode}
        showSections={showSections}
        syncScroll={syncScroll}
        highlightChanges={highlightChanges}
        onViewModeChange={setViewMode}
        onToggleSection={toggleSection}
        onSyncScrollChange={setSyncScroll}
        onHighlightChange={setHighlightChanges}
        onSwapSlots={swapSlots}
      />

      <main className="flex-grow overflow-hidden relative">
        {viewMode === 'split' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 h-full">
            <ComparePanel
              slot={slotA}
              slotLabel="A"
              onChangeSlot={() => onSelectFromHistory?.('A')}
              showSections={showSections}
              highlightChanges={highlightChanges}
              diffData={diffResult?.lines.filter(l => l.type !== 'added')}
              scrollRef={panelARef}
              onScroll={st => handleScroll('A', st)}
            />
            <ComparePanel
              slot={slotB}
              slotLabel="B"
              onChangeSlot={() => onSelectFromHistory?.('B')}
              showSections={showSections}
              highlightChanges={highlightChanges}
              diffData={diffResult?.lines.filter(l => l.type !== 'removed')}
              scrollRef={panelBRef}
              onScroll={st => handleScroll('B', st)}
            />
          </div>
        ) : viewMode === 'unified' ? (
          <div className="h-full overflow-y-auto p-8 max-w-5xl mx-auto w-full custom-scrollbar">
            <DiffRenderer lineDiffs={diffResult?.lines || []} viewMode="unified" />
          </div>
        ) : (
          <div className="h-full grid grid-cols-1 overflow-hidden p-8">
            <div className="bg-slate-900 rounded-2xl border border-slate-800 overflow-y-auto p-8 font-mono text-sm leading-relaxed text-slate-400 custom-scrollbar">
               <div className="flex bg-slate-950 p-2 rounded-xl mb-6 sticky top-0 border border-slate-800 z-10">
                  <Button
                    variant="secondary"
                    size="sm"
                    className="flex-1 font-black uppercase text-[11px] bg-slate-800 text-accent"
                  >
                    Overlay Mode (Slot A Primary)
                  </Button>
               </div>
               <div className="relative">
                 <pre className="whitespace-pre-wrap">{slotA?.lyrics || 'No content in Slot A'}</pre>
                 <div className="absolute inset-0 bg-accent/5 pointer-events-none" />
               </div>
            </div>
          </div>
        )}
      </main>

      {diffResult && <CompareSummary stats={diffResult.stats} sections={diffResult.sections} />}
    </div>
  );
}

export default CompareView;
